<script setup lang="ts">
import type { SplitBtnItem } from '@/components/btns/KSplitBtn.vue';

import { computed, ref } from 'vue';
import { VForm } from 'vuetify/components';

import JobBookingFooterBar from '@/components/bars/JobBookingFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import KSplitBtn from '@/components/btns/KSplitBtn.vue';
import DetailedJobAttachmentsDropZoneField from '@/components/fields/DetailedJobAttachmentsDropZoneField.vue';
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import JobBookingErrorPanel from '@/components/panels/JobBookingErrorPanel.vue';
import JobBookingProductRow from '@/components/rows/JobBookingProductRow.vue';
import AdvancedBookingFormSection from '@/components/sections/AdvancedBookingFormSection.vue';
import JobBookingCustomerSection from '@/components/sections/JobBookingCustomerSection.vue';
import JobBookingProductsSection from '@/components/sections/JobBookingProductsSection.vue';
import JobBookingStopsSection from '@/components/sections/JobBookingStopsSection.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import useJobBookingBookFlow from '@/composables/jobBookings/useJobBookingBookFlow';
import useAdvancedJobBookingView from '@/composables/views/useAdvancedJobBookingView';
import { useDialogStore } from '@/stores/useDialogStore';

const {
  customer,
  stops,
  stopPairs,
  attachments,
  refConfigs,
  addressItems,
  addressLoading,
  products,
  productsForPair,
  addProduct,
  addFinisher,
  removeProduct,
  isSubmitting,
  isLoadingDraft,
  bookingErrors,
  draftId,
  draftName,
  submit,
  reset,
  saveDraft,
  goToDraftsList,
} = useAdvancedJobBookingView();

const dialogStore = useDialogStore();

const form = ref<InstanceType<typeof VForm> | null>(null);
const errorPanel = ref<InstanceType<typeof JobBookingErrorPanel> | null>(null);

const hasScrapProduct = computed(() =>
  products.value.some((product) => product.returns === 'RECYCLE'),
);

function confirmScrapBooking(): Promise<boolean> {
  if (dialogStore.confirmScrapBooking.isDialog) return Promise.resolve(false);
  return new Promise((resolve) => {
    dialogStore
      .open('confirmScrapBooking')
      .confirmed(async () => {
        resolve(true);
      })
      .canceled(async () => {
        resolve(false);
      });
  });
}

const { handleBook, handleBookMenuSelect, handleClearForm, handleSaveDraft } =
  useJobBookingBookFlow({
    form,
    errorPanel,
    stops,
    submit,
    reset,
    saveDraft,
    draftId,
    draftName,
    dialogStore,
    beforeProceed: async () =>
      !hasScrapProduct.value || (await confirmScrapBooking()),
  });

const bookMenuItems: SplitBtnItem[] = [
  {
    title: 'Book & Duplicate',
    value: 'duplicate',
    prependIcon: 'mdi-content-copy',
  },
  {
    title: 'Book & Create New Job',
    value: 'createNew',
    prependIcon: 'mdi-file-plus-outline',
  },
  {
    title: 'Book & Pack',
    value: 'pack',
    prependIcon: 'mdi-package-variant-closed',
    disabled: true,
  },
];
</script>

<template>
  <PageContentWrapper>
    <div v-if="isLoadingDraft" :class="$style.loaderContainer">
      <SimpleLoader :size="64" :width="6" />
    </div>
    <v-form
      v-else
      ref="form"
      validate-on="submit lazy"
      @submit.prevent="handleBook"
    >
      <div
        :class="$style.advancedJobBookingView"
        data-testid="advancedJobBookingView"
      >
        <AdvancedBookingFormSection label="Customer Details">
          <JobBookingCustomerSection
            v-model="customer"
            :ref-configs="refConfigs"
          />
        </AdvancedBookingFormSection>

        <JobBookingStopsSection
          v-model="stops"
          :customer-code="customer.customer_code ?? ''"
          :address-items="addressItems"
          :address-loading="addressLoading"
        />

        <JobBookingProductsSection
          v-for="pair in stopPairs"
          :key="`${pair.fromStop}-${pair.toStop}`"
          :from-stop="pair.fromStop"
          :to-stop="pair.toStop"
          show-add-finisher-btn
          @add-btn-click="addProduct(pair.fromStop, pair.toStop)"
          @add-finisher-btn-click="addFinisher(pair.fromStop, pair.toStop)"
        >
          <JobBookingProductRow
            v-for="entry in productsForPair(pair.fromStop, pair.toStop)"
            :key="entry.index"
            v-model="products[entry.index]"
            :is-return-leg="stops[pair.toStop - 1]?.warehouse ?? false"
            :customer-code="customer.customer_code ?? ''"
            :is-deletable="
              productsForPair(pair.fromStop, pair.toStop).length > 1
            "
            @delete-btn-click="removeProduct(entry.index)"
          />
        </JobBookingProductsSection>

        <AdvancedBookingFormSection label="Attachments">
          <DetailedJobAttachmentsDropZoneField
            v-model="attachments"
            variant="dropzone"
          />
        </AdvancedBookingFormSection>

        <JobBookingErrorPanel
          v-if="bookingErrors.length"
          ref="errorPanel"
          :errors="bookingErrors"
        />
      </div>
    </v-form>

    <JobBookingFooterBar
      v-if="!isLoadingDraft"
      :is-submitting="isSubmitting"
      @clear-form="handleClearForm"
    >
      <KBtn
        variant="outlined"
        priority="primary"
        :disabled="isSubmitting"
        @click="handleSaveDraft"
      >
        Save as draft
      </KBtn>
      <KBtn variant="outlined" priority="primary" @click="goToDraftsList">
        Drafts list
      </KBtn>
      <KSplitBtn
        label="Book job"
        :items="bookMenuItems"
        :loading="isSubmitting"
        @click="handleBook"
        @select="handleBookMenuSelect"
      />
    </JobBookingFooterBar>
  </PageContentWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
.advancedJobBookingView {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1440px;
}

.loaderContainer {
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
